<template>
    <div class="case-news">
        <!-- Busca, ano e total, como na referencia -->
        <v-row dense align="center" class="case-news__toolbar">
            <v-col cols="12" md="7">
                <v-text-field
                    v-model="search"
                    dense
                    outlined
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Pesquisar nas notícias..."
                    @keyup.enter="reload"
                    @click:clear="onClear"
                ></v-text-field>
            </v-col>
            <v-col cols="8" md="3">
                <v-select
                    v-model="year"
                    :items="yearOptions"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    @change="reload"
                ></v-select>
            </v-col>
            <v-col cols="4" md="2" class="text-right">
                <span class="case-news__count"><strong>{{ paging.total }}</strong> {{ paging.total === 1 ? 'notícia' : 'notícias' }}</span>
            </v-col>
        </v-row>

        <div v-if="loading && !items.length" class="text-center py-8">
            <v-progress-circular indeterminate size="32" width="2" color="primary"></v-progress-circular>
        </div>

        <div v-else-if="!items.length" class="text-center py-8 grey--text">
            {{ searched ? 'Nenhuma notícia encontrada.' : 'Nenhuma notícia publicada para este caso.' }}
        </div>

        <article v-for="item in items" :key="item.id" class="case-news__item">
            <div class="case-news__date" aria-label="Data da notícia">
                <span class="case-news__year">{{ datePart(item.published_at, 0) }}</span>
                <span class="case-news__month">{{ datePart(item.published_at, 1) }}</span>
                <span class="case-news__day">{{ datePart(item.published_at, 2) }}</span>
            </div>
            <div class="case-news__body">
                <h3 class="case-news__title">{{ item.title }}</h3>
                <div class="case-news__content" :class="{ 'case-news__content--with-image': item.image_url }">
                    <a v-if="item.image_url" :href="item.image_url" target="_blank" rel="noopener" class="case-news__image">
                        <img :src="item.image_url" :alt="item.title" />
                    </a>
                    <div class="case-news__text-wrap">
                        <div class="case-news__text" :class="{ 'case-news__text--clamped': isLong(item) && !expanded[item.id] }" v-html="item.content"></div>
                        <template v-if="isLong(item)">
                            <button type="button" class="case-news__more" @click="toggle(item)">
                                {{ expanded[item.id] ? 'Ver menos' : 'Ver mais' }}
                                <v-icon small color="primary">{{ expanded[item.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </button>
                            <!-- Texto recolhido: os links de dentro do texto continuam acessiveis -->
                            <div v-if="!expanded[item.id] && linksOf(item).length" class="case-news__links">
                                <span class="case-news__links-label">Links da notícia:</span>
                                <a v-for="(link, index) in linksOf(item)" :key="index" :href="link.href" target="_blank" rel="noopener">{{ link.text }}</a>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </article>

        <div v-if="paging.page < paging.lastPage" class="text-center mt-4">
            <v-btn outlined color="primary" :loading="loading" @click="load(paging.page + 1, true)">Carregar mais notícias</v-btn>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'CaseNews',

    props: {
        clientId: { type: [String, Number], required: true },
    },

    data: () => ({
        items: [],
        years: [],
        loading: false,
        searched: false,
        search: '',
        year: null,
        paging: { page: 1, lastPage: 1, total: 0 },
        expanded: {},
        linksCache: {},
    }),

    computed: {
        yearOptions() {
            return [{ id: null, name: 'Todos os anos' }].concat(this.years.map((item) => ({ id: item, name: String(item) })));
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load(page = 1, append = false) {
            this.loading = true;
            try {
                const params = { page, limit: 10 };
                if (this.year) params.year = this.year;
                if (this.search && this.search.trim()) params.search = this.search.trim();

                const result = await axios.get(`/api/clients/${this.clientId}/news`, { params });
                const content = result.data.content || {};
                const data = content.data || [];
                this.items = append ? this.items.concat(data) : data;
                this.years = content.years || [];
                this.paging = content.paging || { page: 1, lastPage: 1, total: data.length };
                this.searched = !!(params.search || params.year);
            } catch (error) {
                if (!append) this.items = [];
            } finally {
                this.loading = false;
            }
        },

        reload() {
            this.load(1);
        },

        onClear() {
            this.search = '';
            this.load(1);
        },

        // Texto longo fica recolhido com "Ver mais".
        isLong(item) {
            return this.plainText(item.content).length > 500;
        },

        toggle(item) {
            this.$set(this.expanded, item.id, !this.expanded[item.id]);
        },

        plainText(html) {
            return String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        },

        // Links de dentro do texto da noticia (href e rotulo), sem repetir.
        linksOf(item) {
            if (this.linksCache[item.id]) return this.linksCache[item.id];
            const links = [];
            if (typeof DOMParser !== 'undefined') {
                const doc = new DOMParser().parseFromString(String(item.content || ''), 'text/html');
                doc.querySelectorAll('a[href]').forEach((a) => {
                    const href = a.getAttribute('href');
                    if (!href || links.some((l) => l.href === href)) return;
                    links.push({ href, text: (a.textContent || '').trim() || href });
                });
            }
            this.linksCache[item.id] = links;
            return links;
        },

        // published_at vem como AAAA-MM-DD; sem converter fuso.
        datePart(value, index) {
            const parts = String(value || '').slice(0, 10).split('-');
            return parts.length === 3 ? parts[index] : '';
        },
    },
};
</script>

<style scoped>
.case-news {
    margin-top: 24px;
    margin-bottom: 40px;
}

.case-news__toolbar {
    padding-bottom: 16px;
    border-bottom: 1px solid #EFEFEF;
    margin-bottom: 8px;
}

.case-news__count {
    font-size: 14px;
    color: #6B7280;
    white-space: nowrap;
}

.case-news__count strong {
    color: #180032;
}

.case-news__item {
    display: flex;
    gap: 20px;
    padding: 24px 0;
    border-bottom: 1px solid #EFEFEF;
}

.case-news__date {
    flex: 0 0 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 12px 8px;
    border: 1px solid #180032;
    color: #180032;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.1;
    align-self: flex-start;
}

.case-news__year,
.case-news__month {
    border-bottom: 1px solid #180032;
    padding-bottom: 2px;
    min-width: 52px;
    text-align: center;
}

.case-news__body {
    flex: 1;
    min-width: 0;
}

.case-news__title {
    font-size: 18px;
    font-weight: 700;
    color: #180032;
    margin: 0 0 10px;
    line-height: 1.3;
}

.case-news__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.case-news__content--with-image {
    flex-direction: row;
}

.case-news__image {
    flex: 0 0 220px;
    max-width: 220px;
}

.case-news__image img {
    width: 100%;
    height: auto;
    border-radius: 6px;
    display: block;
}

.case-news__text {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #333;
    text-align: justify;
    word-break: break-word;
}

.case-news__text >>> p {
    margin-bottom: 10px;
}

.case-news__text >>> a {
    color: #180032;
    font-weight: 600;
}

.case-news__text-wrap {
    flex: 1;
    min-width: 0;
}

.case-news__text--clamped {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.case-news__more {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-top: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: #180032;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
}

.case-news__links {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #E5E7EB;
    font-size: 14px;
}

.case-news__links-label {
    font-style: italic;
    color: #6B7280;
}

.case-news__links a {
    color: #7E55AB;
    font-weight: 600;
    text-decoration: underline;
    word-break: break-all;
}

@media (max-width: 768px) {
    .case-news__item {
        flex-direction: column;
        gap: 12px;
    }

    .case-news__date {
        flex-direction: row;
        gap: 6px;
        padding: 6px 10px;
        font-size: 16px;
    }

    .case-news__year,
    .case-news__month {
        border-bottom: 0;
        min-width: 0;
    }

    .case-news__year::after,
    .case-news__month::after {
        content: '/';
        margin-left: 6px;
        font-weight: 400;
    }

    .case-news__content--with-image {
        flex-direction: column;
    }

    .case-news__image {
        max-width: 100%;
        flex-basis: auto;
    }
}
</style>
